import { api, getToken } from "./api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toWsUrl(base) {
  try {
    const u = new URL(base);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    return u.toString().replace(/\/$/, "");
  } catch {
    return base.replace(/^http/, "ws");
  }
}

/**
 * Connects to /api/ws?token=... and falls back to polling if the WS
 * cannot establish within a short window or errors out.
 *
 * Emits high-level events:
 *   onMessage(message)        — a new message arrived
 *   onRead({conversation_id, reader_id, read_at})
 *   onStatus({connected, mode})   mode: "ws" | "polling"
 *
 * Polling loop calls onPoll() every `pollInterval` ms.
 */
export function createRealtime({
  onMessage,
  onRead,
  onPresence,
  onTyping,
  onConversationNew,
  onPulseEvent,
  onStatus,
  onPoll,
  pollInterval = 3000,
} = {}) {
  let ws = null;
  let closed = false;
  let mode = "connecting";
  let pollTimer = null;
  let reconnectTimer = null;
  let wsAttempts = 0;

  function setMode(next) {
    if (mode === next) return;
    mode = next;
    onStatus && onStatus({ connected: next === "ws" || next === "polling", mode: next });
  }

  function startPolling() {
    if (pollTimer) return;
    setMode("polling");
    // Fire an immediate poll, then interval
    const tick = async () => {
      if (closed) return;
      try {
        onPoll && (await onPoll());
      } catch (_e) {
        /* ignore */
      }
    };
    tick();
    pollTimer = setInterval(tick, pollInterval);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function connectWs() {
    if (closed) return;
    const token = getToken();
    if (!token) {
      startPolling();
      return;
    }
    const url = `${toWsUrl(BACKEND_URL)}/api/ws?token=${encodeURIComponent(token)}`;
    let didOpen = false;
    try {
      ws = new WebSocket(url);
    } catch (_e) {
      startPolling();
      return;
    }
    const openTimeout = setTimeout(() => {
      if (!didOpen) {
        try {
          ws && ws.close();
        } catch (_e) {
          /* ignore */
        }
        wsAttempts += 1;
        if (wsAttempts >= 2) {
          startPolling();
        } else {
          reconnectTimer = setTimeout(connectWs, 1500);
        }
      }
    }, 4000);

    ws.onopen = () => {
      didOpen = true;
      clearTimeout(openTimeout);
      wsAttempts = 0;
      stopPolling();
      setMode("ws");
    };
    ws.onmessage = (ev) => {
      let data;
      try {
        data = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (data.type === "message.new" && data.message) {
        onMessage && onMessage(data.message);
      } else if (data.type === "message.read") {
        onRead && onRead(data);
      } else if (data.type === "presence.change") {
        onPresence && onPresence(data);
      } else if (data.type === "typing" && data.conversation_id) {
        onTyping && onTyping(data);
      } else if (data.type === "conversation.new" && data.conversation) {
        onConversationNew && onConversationNew(data.conversation);
      } else if (data.type && data.type.startsWith("pulse.")) {
        onPulseEvent && onPulseEvent(data);
      } else if (data.type === "tokens.update") {
        // Broadcast a global window event so components deep in the tree
        // (AuthContext, tokens chip) can react without prop-drilling this
        // rare event through the realtime plumbing.
        try {
          window.dispatchEvent(new CustomEvent("queonda.tokens.update", { detail: data }));
        } catch (_e) {
          /* ignore */
        }
      }
    };
    ws.onerror = () => {
      /* handled by onclose */
    };
    ws.onclose = () => {
      clearTimeout(openTimeout);
      ws = null;
      if (closed) return;
      // If we never got a message, fall back to polling. Otherwise try to reconnect a couple times.
      if (mode === "ws") {
        reconnectTimer = setTimeout(connectWs, 1500);
      } else {
        wsAttempts += 1;
        if (wsAttempts >= 2) startPolling();
        else reconnectTimer = setTimeout(connectWs, 1500);
      }
    };
  }

  connectWs();

  return {
    close() {
      closed = true;
      stopPolling();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        ws && ws.close();
      } catch (_e) {
        /* ignore */
      }
    },
    mode: () => mode,
    send(payload) {
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(payload));
          return true;
        }
      } catch (_e) {
        /* ignore */
      }
      return false;
    },
  };
}

/* ---------- REST helpers used by pages ---------- */

export const chatApi = {
  searchUsers: async (q) => (await api.get(`/users/search?q=${encodeURIComponent(q)}`)).data,
  listConversations: async () => (await api.get("/conversations")).data,
  createConversation: async (to_user_id) =>
    (await api.post("/conversations", { to_user_id })).data,
  listMessages: async (convId, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.limit) params.set("limit", opts.limit);
    if (opts.before) params.set("before", opts.before);
    const qs = params.toString();
    return (await api.get(`/conversations/${convId}/messages${qs ? `?${qs}` : ""}`)).data;
  },
  sendMessage: async (convId, text) =>
    (await api.post(`/conversations/${convId}/messages`, { text })).data,
  markRead: async (convId) => (await api.post(`/conversations/${convId}/read`)).data,
  getPresence: async (userId) => (await api.get(`/presence/${userId}`)).data,
};
